// /src/components/InvoicesOfShippedContainers.js
import { useState, useEffect, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@material-ui/core";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Assets
import analyzeIcon from "../../../assets/analyzeIcon.png";
import uploadIcon from "../../../assets/upload_icon.png";
import closeIcon from "../../../assets/close_icon.png";
import deleteIcon from "../../../assets/circled_cancel.svg";
import containersIcon from "../../../assets/container.png";
import addIconLight from "../../../assets/add_icon_light.png";
import nextIcon from "../../../assets/next_icon.png";
import previousIcon from "../../../assets/previous_icon.png";
import infoIcon from "../../../assets/information.png";

// Styles
import classes from "../../../components/Modal.module.css";
import classes3 from "../../../components/PDFUploader.module.css";

// Page sections
import InformationModal from "./InformationModal";

// Utils
import { UserAuth } from "../../../utils/AuthContext";
import { useDocs } from "../../../utils/useDocs";
import { DataContext } from "../../../utils/DataContext";
import useLanguage from "../../../utils/useLanguage";
import { formatDate } from "../../../utils/toDate";
import { toMoney } from "../../../utils/toMoney";

// Components
import Dropdown from "../../../components/Dropdown";
import BackToDashboard from "../../../components/BackToDashboard";
import Loader from "../../../components/Loader";
import CustomLink from "../../../components/CustomLink";
import UserSection from "../../../components/UserSection";
import InformationBox from "../../../components/InformationBox";
import MultipleSearchFilter from "../../../components/MultipleSearchFilter";
import ChartModal from "../../../components/ChartModal";
import CustomButton from "../../../components/CustomButton";
import AllDocumentsSlider from "../../../components/AllDocumentsSlider";
import HotDropdown from "../../../components/HotDropdown";
import LoaderWithProgress from "../../../components/LoaderWithProgress";

const InvoicesOfMultiContainers = () => {
  // New state variables for the row modal
  const [selectedRow, setSelectedRow] = useState(null);
  const [showRowModal, setShowRowModal] = useState(false);

  const [pdfFile, setPdfFile] = useState(null);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [extractedData, setExtractedData] = useState({});
  const [isAnalyzable, setIsAnalyzable] = useState(false);

  const [docsFields, setDocsFields] = useState({
    count: 1,
    max: 10,
  });
  const [tempDocs, setTempDocs] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [showBtns, setShowBtns] = useState(false);

  const { handlePrepareDocs } = useDocs();

  const { userData } = useContext(UserAuth);

  const navigate = useNavigate();

  // const token = localStorage.getItem("token");
  // const userId = parseInt(localStorage.getItem("userId"), 10);
  // const supplierId = parseInt(localStorage.getItem("supplierId"), 10);

  const show3Buttons = () => {
    setShowBtns(true);
  };

  // function to delete the files preupload
  const handleDeleteFile = (index) => {
    setTempDocs((prev) => {
      const newDocs = [...prev];
      newDocs.splice(index, 1); // Remove the file at the given index
      return newDocs;
    });

    // resetting the input element to empty
    const fileInput = document.getElementById(`invoiceDoc${index + 1}`);
    if (fileInput) {
      fileInput.value = "";
    }

    setPdfFile(null);
    setIsAnalyzable(false);
  };

  const generateUploadInputs = () => {
    return Array.from({ length: docsFields.count }).map((_, index) => (
      <>
        <form className="o-column" key={index}>
          <div className="o-row o-row--vertical-align_center o-row--gap-size_400">
            <input
              accept="application/pdf, image/*"
              type="file"
              onChange={(e) => {
                const file = e.target.files[0];
                setPdfFile(file);
                handlePrepareDocs(
                  e,
                  // `Invoice ${invoiceNumber} - image ${index + 1}`
                  `image ${index + 1}`
                );
                setTempDocs((prev) => [...prev, file]);

                // Enable the Analyze button
                setIsAnalyzable(true);
              }}
              className={classes3.fileInput}
              name="images"
              id={`invoiceDoc${index + 1}`}
              disabled={tempDocs.length > 0}
            />
            <div>
              <label htmlFor={`invoiceDoc${index + 1}`}>
                <p
                  // className={classes3.customFileInput}
                  className={`${
                    tempDocs.length > 0 ? classes3.buttonDisabled : ""
                  } ${classes3.customFileInput}`}
                >
                  <img src={uploadIcon} alt={`Upload image ${index + 1}`} />
                  <span>
                    {t("uploadDocument")} {index + 1}
                  </span>
                </p>
              </label>
            </div>

            <CustomButton
              title={t("analyzeInvoice")}
              icon={analyzeIcon}
              variant="blank"
              onClick={handleUpload}
              type="submit"
              disabled={!isAnalyzable}
            />

            <CustomButton
              title={t("cancel")}
              variant="blank"
              icon={closeIcon}
              iconSize={14}
              onClick={() => {
                setShowBtns(false);
                handleDeleteFile(index);
              }}
            />
          </div>
          {analyzeLoading && <LoaderWithProgress />}
          {tempDocs[index] && (
            <div className={classes3.fileName}>
              {t("fileName")} : {tempDocs[index]?.name?.slice(0, 30)}
              <button
                className={classes3.deleteFileButton}
                onClick={() => handleDeleteFile(index)}
              >
                <img src={deleteIcon} />
              </button>
            </div>
          )}
        </form>
      </>
    ));
  };

  const handleUpload = async (event) => {
    if (event) event.preventDefault();

    if (!pdfFile) {
      console.error("No file selected!");
      return;
    }

    const formData = new FormData();
    formData.append("image", pdfFile);

    try {
      setAnalyzeLoading(true);
      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_URL}/extractPdf`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${userData?.token}`,
            "User-Id": userData?.userId,
            "Supplier-Id": selectedSupplier?.id,
          },
        }
      );

      if (uploadResponse.status === 200) {
        setExtractedData(uploadResponse.data);
        navigate("/analyzedinvoice", {
          state: { extractedData: uploadResponse.data }, // Pass correct data
        });
      }
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setAnalyzeLoading(false);
    }
  };

  // Handler for opening the row info modal
  const handleRowButtonClick = (row) => {
    setSelectedRow(row);
    setShowRowModal(true);
  };

  // Handler for closing the row info modal
  const handleCloseRowModal = () => {
    setSelectedRow(null);
    setShowRowModal(false);
  };

  const rows = [
    {
      title: "invoiceNumber",
      value: "invoiceNumber",
    },
    {
      title: "amountForSupplier",
      value: "amountForSupplier",
    },
    {
      title: "abstractAmountForCustomer",
      value: "abstractAmountForCustomer",
    },
    {
      title: "received",
      value: "received",
    },
    {
      title: "left",
      value: "left",
    },
    {
      title: "containerNumber",
      value: "containerNumber",
    },
    {
      title: "invoiceDate",
      value: "invoiceDate",
    },
    {
      title: "company",
      value: "subCompany",
    },
    {
      title: "notes",
      value: "notes",
    },
    {
      title: "createdBy",
      value: "createdBy",
    },
    {
      title: "print",
      value: "print",
    },
  ];

  const { t } = useTranslation("general");
  const { currentDirection } = useLanguage();

  // const [data, setData] = useState([]);
  const [newRow, setNewRow] = useState({
    balance: 0,
    containersPricesInRMB: 0,
    currentBalance: 0,
    date: new Date(),
    notes: "",
  });

  const [showInputFields, setShowInputFields] = useState(false);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [tempCalc, setTempCalc] = useState(0);

  // const [invoicesCurrentPage, setInvoicesCurrentPage] = useState(1);
  // const [invoicesTotalPages, setInvoicesTotalPages] = useState(1);
  // const [totalRecords, setTotalRecords] = useState(1);
  // const [balances, setBalances] = useState(null);
  const [searchQuery, setSearchQuery] = useState(null);
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("invoiceNumber"),
    value: "invoiceNumber",
  });

  const [startDate, setStartDate] = useState(formatDate(new Date()));
  const [endDate, setEndDate] = useState(formatDate(new Date()));

  const searchTitles = useMemo(
    () => [
      {
        title: t("invoiceNumber"),
        value: "invoiceNumber",
        type: "text",
      },
      {
        title: t("amountForSupplier"),
        value: "amountForSupplier",
        type: "moneyV2",
      },
    ],
    [t]
  );

  const {
    setSupplierModal,
    selectedSupplier,
    error,
    selectedSubCompany,
    getSubCompanies,
    loading,
    getCustomers,
    selectedCustomer,
    setSelectedCustomer,
    customers,
    currentBalance,
    handleSelectCustomer,
    shippedContainersCount,
    setShippedContainersCurrentPage,
    setSelectedSupplier,
    fetchArchivedInvoicesOfMultiContainers,
    selectedInvoicesOfMultiContsStatus,
    setSelectedInvoicesOfMultiContsStatus,
    fetchInvoicesOfMultiContainers,
    invoicesOfMultiContainers: data,
    balancesOfInvOfMultiConts: balances,
    setBalancesOfInvOfMultiConts: setBalances,
    totalInvOfMultiContsRecords: totalRecords,
    setTotalInvOfMultiContsRecords: setTotalRecords,
    totalPagesOfInvOfMultiConts: invoicesTotalPages,
    currentPageOfInvOfMultiConts: invoicesCurrentPage,
    setCurrentPageOfInvOfMultiConts: setInvoicesCurrentPage,
  } = useContext(DataContext);

  // * Reset to default
  useEffect(() => {
    return () => {
      setSelectedInvoicesOfMultiContsStatus({
        title: "Active",
        value: "active",
      });
    };
  }, []);

  const balanceStyle =
    currentBalance?.value >= 0 ? "positive-value" : "negative-value";

  useEffect(() => {
    const getNewInvoicesPage = async () => {
      let data = await getAllInvoices();
      setSelectedCustomer(data?.currentCustomer);
      setSelectedSupplier(data?.currentSupplier);
    };
    getNewInvoicesPage();
  }, []);

  useEffect(() => {
    const getNewInvoicesPage = async () => await getAllInvoices();
    if (selectedSupplier?.id) {
      getNewInvoicesPage();
    }
  }, [invoicesCurrentPage]);

  const handleChangeStatus = (status) => {
    const { value } = status;
    setSelectedInvoicesOfMultiContsStatus(status);
    setInvoicesCurrentPage(1);
    setSearchQuery("");

    if (value === "active") {
      // setShowAddInvoiceButton(true);
      const req = async () => {
        const response = await fetchInvoicesOfMultiContainers(
          selectedSupplier?.id,
          selectedCustomer?.id,
          selectedSubCompany?.id,
          null,
          null,
          startDate,
          endDate
        );
        setBalances(response?.balance?._sum);
      };
      req();
    } else if (value === "archived") {
      // setShowAddInvoiceButton(false);

      const req = async () => {
        const response = await fetchArchivedInvoicesOfMultiContainers(
          selectedSupplier?.id,
          selectedCustomer?.id,
          selectedSubCompany?.id,
          selectedTitle.value,
          null,
          startDate,
          endDate
        );
        // setBalances(response?.balance?._sum);
      };
      req();
      //   selectedSupplier?.id,
      //   selectedCustomer?.id
      // );
    }
  };

  const getAllInvoices = async () => {
    const res = await fetchInvoicesOfMultiContainers(
      selectedSupplier?.id,
      selectedCustomer?.id,
      selectedSubCompany?.id
    );

    return res;
  };
  const handleCancel = (modal) => {
    if (modal === "supplier") {
      setSupplierModal(false);
    } else {
      setNewRow({
        balance: 0,
        containersPricesInRMB: 0,
        currentBalance: 0,
        date: new Date(),
        notes: "",
      });
      setShowInputFields(false);
    }
  };

  const handleShowChart = () => {
    setChartModalOpen(true);
  };

  const handleCloseChart = () => {
    setChartModalOpen(false);
  };

  const handleSearchType = async (type) => {
    setSelectedTitle(type);
  };

  const handleSearch = async (query = searchQuery) => {
    if (typeof query !== "string") {
      query = searchQuery;
    }

    let searchBy;

    if (selectedTitle.value === "date") {
      searchBy = selectedDate;
    } else {
      searchBy = query;
    }

    if (selectedInvoicesOfMultiContsStatus.value === "active") {
      await fetchInvoicesOfMultiContainers(
        selectedSupplier?.id,
        selectedCustomer?.id,
        selectedSubCompany?.id,
        selectedTitle.value,
        searchBy,
        startDate,
        endDate
      );
    } else {
      await fetchArchivedInvoicesOfMultiContainers(
        selectedSupplier?.id,
        selectedCustomer?.id,
        selectedSubCompany?.id,
        selectedTitle.value,
        searchBy,
        startDate,
        endDate
      );
    }
  };

  const hiddenBalances =
    selectedCustomer?.id === "All" && selectedSupplier?.id === "All";
  return (
    <>
      <main className="e-container">
        <div>
          {/*<BackToDashboard*/}
          {/*  title={t("back")}*/}
          {/*  variant="blank"*/}
          {/*  icon={currentDirection === "ltr" ? previousIcon : nextIcon}*/}
          {/*  iconSize={14}*/}
          {/*/>*/}
        </div>
        <div className="invoice-table-container">
          <UserSection
            handleShowChart={handleShowChart}
            page="invoicesOfMultiContainers"
          />
          <section className="o-row o-row--vertical-align_center u-margin--bottom_300">
            <label className="u-min-width-specific_50">{t("customer")}: </label>
            {selectedCustomer?.id ? (
              <HotDropdown
                initOptions={[{ id: "All", name: "All" }, ...customers]}
                selected={selectedCustomer}
                setSelected={(customer) =>
                  handleSelectCustomer(customer, "invoicesOfMultiContainers")
                }
                errorText="Failed to get"
                optionsName={"customers"}
                // thick
                getData={getCustomers}
              />
            ) : (
              <span>{t("noCustomersFound")}</span>
            )}
          </section>
          <div className="under-user-section">
            <div className="page-title">{t("invoicesPage")}</div>
            <CustomButton
              elementType={Link}
              title={t("containers")}
              to="/orgShippedContainers"
              icon={containersIcon}
            />
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              {!selectedSupplier?.id ? (
                <div className="full-w-button-container">
                  <div className="column-flex-box flex-center-center">
                    <InformationBox>{t("noSuppliersAddOne")}</InformationBox>
                    <CustomButton
                      title={t("goToSuppliersPage")}
                      minWidth={140}
                      variant="blank"
                      elementType={Link}
                      to={"/viewSuppliersPage"}
                    />
                  </div>
                </div>
              ) : (
                <>
              <div className="top-action-buttons responsive-box">
                    <div className="button-group">
                      <CustomButton
                        title={t("createInvoice")}
                        icon={addIconLight}
                        iconSize={20}
                        elementType={Link}
                        to="/createInvoiceOfMultiContsPage"
                      />

                      {!showBtns && (
                        <CustomButton
                          title={t("analyzeInvoice")}
                          icon={analyzeIcon}
                          variant="blank"
                          onClick={show3Buttons}
                          iconSize={18}
                        />
                      )}
                    </div>

                    <section className="col title-col ">
                      <label>{t("invoicesStatus")}: </label>
                      <Dropdown
                        options={[
                          { title: "Active", value: "active" },
                          { title: "Archived", value: "archived" },
                        ]}
                        selected={selectedInvoicesOfMultiContsStatus}
                        setSelected={handleChangeStatus}
                        thick
                      />
                    </section>
                  </div>

                  {showBtns && (
                    <div className="o-row o-row--gap-size_200">
                      <section className="u-margin--top_500 u-margin--bottom_500 full-width">
                        <div className={classes.pdfUploaderSection}>
                          <span>{t("uploadDocs")}: </span>
                          <div className={classes.pdfUploaderSectionActions}>
                            {generateUploadInputs()}
                          </div>
                        </div>
                      </section>

                      {documents.length ? (
                        <AllDocumentsSlider
                          documents={documents}
                          title="All Documents"
                        />
                      ) : null}
                    </div>
                  )}

                  <div className="u-padding--bottom_500">
                    <MultipleSearchFilter
                      options={searchTitles}
                      selected={selectedTitle}
                      setSelected={handleSearchType}
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                      thick
                      getData={() => {}}
                      handleSearch={handleSearch}
                      setStartDate={setStartDate}
                      setEndDate={setEndDate}
                      startDate={startDate}
                      endDate={endDate}
                      flex
                    />
                  </div>

                  {data?.length !== 0 ? (
                    <>
                      <TableContainer component={Paper}>
                        <Table className="c-table">
                          <TableHead>
                            <TableRow>
                              {rows.map((row, index) => {
                                if (
                                  row?.title !== "company" ||
                                  selectedSubCompany?.id === "All"
                                ) {
                                  return (
                                    <TableCell
                                      key={index}
                                      className="bold-text cell-title"
                                    >
                                      {t(row.title)}
                                    </TableCell>
                                  );
                                }
                              })}
                            </TableRow>
                          </TableHead>
                          <TableBody className="table-body">
                            {data?.map((row, index) => (
                              <TableRow key={index}>
                                <TableCell className="sharp-cell text-center">
                                  {!row.archived ? (
                                    <CustomLink
                                      to={`/viewMultiContainerInvoice/${row.id}`}
                                    >
                                      <p className="link">
                                        {row?.invoiceNumber}
                                      </p>
                                    </CustomLink>
                                  ) : (
                                    <p className="archived-invoice">
                                      {row?.invoiceNumber}
                                    </p>
                                  )}
                                </TableCell>

                                <TableCell className="cell">
                                  <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                                    <div className="t-text-standard">
                                      {row?.amountForSupplier}
                                    </div>
                                    <div className="t-bold_bold">
                                      {row?.supplier?.currency}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="cell">
                                  <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                                    <div className="t-text-standard">
                                      {row?.abstractAmountForCustomer}
                                    </div>
                                    <div className="t-bold_bold">
                                      {row?.customer?.currency}
                                    </div>
                                  </div>
                                </TableCell>

                                <TableCell className="cell">
                                  <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                                    <div className="t-text-standard">
                                      {row?.received}
                                    </div>
                                    <div className="t-bold_bold">
                                      {row?.customer?.currency}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="cell">
                                  <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                                    <div className="t-text-standard">
                                      {row?.left}
                                    </div>
                                    <div className="t-bold_bold">
                                      {row?.customer?.currency}
                                    </div>
                                  </div>
                                </TableCell>

                                <TableCell>
                                  {row.ShippedContainer &&
                                  row.ShippedContainer.length > 0 ? (
                                    row.ShippedContainer.map(
                                      (container, index) => (
                                        <div
                                          key={index}
                                          className="o-column o-column--vertical-align_start o-column--gap-size_200 u-margin--bottom_300"
                                        >
                                          <span className="notes-bold">
                                            {t("container")} {index + 1} :
                                          </span>
                                          <span>
                                            {container.containerNumber}
                                          </span>
                                        </div>
                                      )
                                    )
                                  ) : (
                                    <span className="o-row t-text-align_center">
                                      {t("noAddedContainers")}
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell
                                  className="cell"
                                  style={{ height: "50px", minWidth: "145px" }}
                                >
                                  {row.date || row.modifiedAt
                                    ? new Date(row.date || row.modifiedAt)
                                        .toISOString()
                                        .split("T")[0]
                                    : ""}
                                </TableCell>
                                {selectedSubCompany?.id === "All" && (
                                  <td className="cell mobile-hider ">
                                    {row?.subCompany?.nameInAr || "-"}
                                  </td>
                                )}

                                <TableCell className="text-start">
                                  {row.notes}
                                </TableCell>
                                <TableCell
                                  className="cell"
                                  style={{ height: "50px", minWidth: "145px" }}
                                >
                                  {row?.user?.username}
                                </TableCell>
                                {/* New TableCell for the action button */}
                                <TableCell className="cell">
                                  <CustomButton
                                    variant="blank"
                                    title={t("ViewInfo")}
                                    onClick={() => handleRowButtonClick(row)}
                                    icon={infoIcon}
                                    iconSize={15}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <section className="pagination">
                        <div className="pagination-buttons">
                          <CustomButton
                            variant="blank"
                            title={t("previous")}
                            onClick={() =>
                              setInvoicesCurrentPage((prev) => prev - 1)
                            }
                            disabled={invoicesCurrentPage === 1}
                            icon={
                              currentDirection === "ltr"
                                ? previousIcon
                                : nextIcon
                            }
                            iconSize={15}
                          />
                          <span className="pagination-location">
                            {t("page")}: {invoicesCurrentPage} -{" "}
                            {invoicesTotalPages}
                          </span>
                          <CustomButton
                            title={t("next")}
                            variant="blank"
                            onClick={() =>
                              setInvoicesCurrentPage((prev) => prev + 1)
                            }
                            disabled={
                              invoicesCurrentPage === invoicesTotalPages
                            }
                            icon={
                              currentDirection === "ltr"
                                ? nextIcon
                                : previousIcon
                            }
                            iconSize={15}
                            reverseIcon
                          />
                        </div>

                        <div className="pagination-buttons">
                          <span className="pagination-location">
                            {t("totalRecords")}: {totalRecords}
                          </span>
                        </div>
                      </section>

                      {!hiddenBalances && selectedSupplier?.id && (
                        <div className="balances margin-inline o-column o-column--vertical-align_end o-column--gap-size_400 u-margin--top_400">
                          {/* <p className="balance">
                            <span>{t("customerCurrentBalance")}: </span>
                            <span className={balanceStyle}>
                              {!selectedCustomer?.currency ? (
                                t("noCustomerSelected")
                              ) : (
                                <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                                  <span>
                                    {toMoney(balances?.finalAmountForCustomer)}
                                  </span>
                                  <span>{selectedCustomer?.currency}</span>
                                </div>
                              )}
                            </span>
                          </p> */}

                          <div className="balance">
                            <span>{t("supplierCurrentBalance")}: </span>
                            <span className={balanceStyle}>
                              {!selectedSupplier?.currency ||
                              selectedSupplier?.currency === "N/A" ? (
                                t("noSupplierSelected")
                              ) : (
                                <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                                  <span>
                                    {toMoney(balances?.amountForSupplier)}
                                  </span>
                                  <span>{selectedSupplier?.currency}</span>
                                </div>
                              )}
                            </span>
                          </div>
                          <div className="balance">
                            <span>{t("amountForCustomer")}: </span>
                            <span className={balanceStyle}>
                              {!selectedCustomer?.currency ? (
                                t("noCustomerSelected")
                              ) : (
                                <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                                  <span>
                                    {toMoney(
                                      balances?.abstractAmountForCustomer
                                    )}
                                  </span>
                                  <span>{selectedCustomer?.currency}</span>
                                </div>
                              )}
                            </span>
                          </div>
                          <div className="balance">
                            <span>{t("totalLeftAmountWithCustomer")}: </span>
                            <span className={balanceStyle}>
                              {!selectedCustomer?.currency ? (
                                t("noCustomerSelected")
                              ) : (
                                <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                                  <span>{toMoney(balances?.left)}</span>
                                  <span>{selectedCustomer?.currency}</span>
                                </div>
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <InformationBox>{t("noTransactions")}</InformationBox>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </main>

      {/* Render the ChartModal component */}
      <ChartModal
        open={chartModalOpen}
        onClose={handleCloseChart}
        data={data}
      />

      {/* Render the Printing modal */}
      {showRowModal && selectedRow && (
        <InformationModal
          selectedRow={selectedRow}
          handleClose={handleCloseRowModal}
        />
      )}
    </>
  );
};

export default InvoicesOfMultiContainers;
